
import React, { useState, useEffect } from 'react';
import { useApp, User, Role, GlobalSettings, Appointment, formatHumaneRovitaLicenseId, isUserDisabled } from './AppContext';
import { SectionTitle, Button } from './Components';
import { AdminPasswordModal } from './AdminPasswordModal';

// --- SUB-COMPONENTS ---

const ReplyModal = ({ message, onClose, onReply }: { message: any, onClose: () => void, onReply: (id: string, email: string, reply: string) => Promise<void> }) => {
    const [reply, setReply] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'auto'; };
    }, []);

    const { showToast } = useApp();

    const handleSubmit = async () => {
        if (!reply.trim()) return;
        setIsSubmitting(true);
        try {
            await onReply(message.id, message.email, reply);
            onClose();
        } catch(e: any) {
            showToast("Failed to Send", e.message || "Failed to send reply", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[600] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300">
            {/* Modal Card: Constrained height, flex-col, and proper scroll isolation */}
            <div className="bg-white w-full max-w-lg rounded-[1.8rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col my-auto max-h-[calc(100dvh-1.25rem)] sm:max-h-[calc(100dvh-2rem)] md:max-h-[85vh] border border-slate-100 relative">
                <div className="p-4 sm:p-6 md:p-8 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900 tracking-tight">Reply to Message</h3>
                        <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">To: {message.name} ({message.email})</p>
                    </div>
                    <button onClick={onClose} className="text-slate-300 hover:text-slate-500 transition-colors p-2 rounded-xl"><i className="fa-solid fa-xmark text-xl"></i></button>
                </div>
                <div className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 overflow-y-auto flex-1 scrollbar-thin min-h-0">
                    <div className="bg-slate-50 p-4 sm:p-6 rounded-2xl border border-slate-100">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Original Inquiry:</p>
                        <p className="text-slate-600 italic font-medium leading-relaxed text-xs sm:text-sm">"{message.message}"</p>
                    </div>
                    
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Your Response</label>
                        <textarea 
                            rows={5}
                            className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base resize-none"
                            placeholder="Type your official medical response here..."
                            value={reply}
                            onChange={e => setReply(e.target.value)}
                        />
                    </div>
                </div>

                <div className="p-4 sm:p-6 md:p-8 bg-slate-50/90 backdrop-blur-md flex gap-3 sm:gap-4 shrink-0 border-t border-slate-100 z-10">
                    <Button onClick={onClose} variant="ghost" className="flex-1 font-bold py-3 text-sm">Cancel</Button>
                    <Button onClick={handleSubmit} disabled={isSubmitting || !reply.trim()} className="flex-[2] py-3 text-sm shadow-lg shadow-indigo-100 flex items-center justify-center gap-2">
                        {isSubmitting ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-paper-plane"></i>}
                        {isSubmitting ? 'Sending...' : 'Send Secure Reply'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const UserCreateModal = ({ onClose, onSave }: { onClose: () => void, onSave: (data: any) => Promise<void> }) => {
    const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'patient' as Role });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'auto'; };
    }, []);

    const { showToast } = useApp();

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await onSave(formData);
            onClose();
        } catch(e: any) {
            showToast("Failed to Create", e.message || "Failed to create user", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[600] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-lg rounded-[1.8rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col my-auto max-h-[calc(100dvh-1.25rem)] sm:max-h-[calc(100dvh-2rem)] md:max-h-[85vh] border border-slate-100 relative">
                <div className="p-4 sm:p-6 md:p-8 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900 tracking-tight">Create User</h3>
                        <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">Add User Account</p>
                    </div>
                    <button onClick={onClose} className="text-slate-300 hover:text-slate-500 transition-colors p-2 rounded-xl"><i className="fa-solid fa-xmark text-xl"></i></button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="flex flex-col flex-1 min-h-0 overflow-hidden">
                    <div className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-5 overflow-y-auto flex-1 scrollbar-thin min-h-0">
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Full Name</label>
                            <input className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" placeholder="e.g. John Doe" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Email Address</label>
                            <input type="email" className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" placeholder="email@rovita.care" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Security Password</label>
                            <input type="password" className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" placeholder="••••••••" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} required />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Account Type</label>
                            <select className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold appearance-none text-sm sm:text-base bg-slate-50 border-2 border-transparent focus:border-indigo-500" value={formData.role} onChange={e => setFormData({...formData, role: e.target.value as Role})}>
                                <option value="patient">Registered Patient</option>
                                <option value="doctor">Clinic Doctor</option>
                                <option value="admin">Platform Administrator</option>
                            </select>
                        </div>
                    </div>
                    <div className="p-4 sm:p-6 md:p-8 bg-slate-50/90 backdrop-blur-md flex gap-3 sm:gap-4 shrink-0 border-t border-slate-100 z-10">
                        <Button type="button" onClick={onClose} variant="ghost" className="flex-1 font-bold py-3 text-sm">Cancel</Button>
                        <Button type="submit" disabled={isSubmitting || !formData.name || !formData.email} className="flex-[2] py-3 text-sm shadow-lg shadow-indigo-100 flex items-center justify-center gap-2">
                            {isSubmitting ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-user-plus"></i>}
                            {isSubmitting ? 'Creating Account...' : 'Create Account'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const UserEditModal = ({ user, onClose, onSave, onOpenPasswordModal }: { user: User, onClose: () => void, onSave: (id: string, data: Partial<User>) => void, onOpenPasswordModal?: (user: User) => void }) => {
    const [formData, setFormData] = useState({
        name: user.name,
        email: user.email,
        role: user.role,
        specialties: user.specialties || [],
        phone: user.phone || '',
        address: user.address || '',
        isVerified: user.isVerified || false,
        isDisabled: Boolean(user.isDisabled || user.disabled || user.status === 'suspended' || user.status === 'revoked'),
        licenseNumber: user.licenseNumber || user.medicalLicenseNumber || '',
        bio: user.bio || '',
        sessionRate: user.sessionRate || 50
    });

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'auto'; };
    }, []);

    const availableSpecialties = ['General Physician', 'Nephrology', 'Cardiology', 'Neurology', 'Dentist', 'Pediatrics', 'Mental Health', 'Oncology', 'Dermatology', 'Orthopedics', 'Urology', 'Gastroenterology'];

    const handleSpecialtyToggle = (s: string) => {
        const next = formData.specialties.includes(s) 
            ? formData.specialties.filter(x => x !== s) 
            : [...formData.specialties, s];
        setFormData({...formData, specialties: next});
    };

    return (
        <div className="fixed inset-0 z-[600] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-3xl rounded-[1.8rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col my-auto max-h-[calc(100dvh-1.25rem)] sm:max-h-[calc(100dvh-2rem)] md:max-h-[85vh] border border-slate-100 relative">
                <div className="p-4 sm:p-6 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900 tracking-tight">Edit User Profile</h3>
                        <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">UID: {user.id.slice(0, 8)}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-300 hover:text-slate-500 transition-colors bg-white w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shadow-sm border border-slate-100"><i className="fa-solid fa-xmark text-lg sm:text-xl"></i></button>
                </div>
                
                <div className="p-4 sm:p-6 md:p-8 space-y-4 overflow-y-auto flex-1 scrollbar-thin min-h-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        <div className="p-3.5 sm:p-4 bg-indigo-50 rounded-2xl border border-indigo-100 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black uppercase text-indigo-400 tracking-widest">Medical Record (MRN)</p>
                                <p className="text-sm sm:text-base md:text-lg font-black text-indigo-900 leading-none mt-1">{user.mrn || 'N/A'}</p>
                            </div>
                            <i className="fa-solid fa-barcode text-xl sm:text-2xl text-indigo-200"></i>
                        </div>
                        {formData.role === 'doctor' && (
                            <div className="p-3.5 sm:p-4 bg-emerald-50 rounded-2xl border border-emerald-100 flex justify-between items-center">
                                <div>
                                    <p className="text-[10px] font-black uppercase text-emerald-400 tracking-widest">Session Rate ($)</p>
                                    <input 
                                        type="number" 
                                        className="bg-transparent border-none outline-none text-sm sm:text-base md:text-lg font-black text-emerald-900 w-24 p-0" 
                                        value={formData.sessionRate} 
                                        onChange={e => setFormData({...formData, sessionRate: Number(e.target.value)})} 
                                    />
                                </div>
                                <i className="fa-solid fa-dollar-sign text-xl sm:text-2xl text-emerald-200"></i>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Full Name</label>
                            <input className="w-full p-3 sm:p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Email</label>
                            <input className="w-full p-3 sm:p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Role</label>
                            <select className="w-full p-3 sm:p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" value={formData.role} onChange={e => setFormData({...formData, role: e.target.value as Role})}>
                                <option value="patient">Patient</option>
                                <option value="doctor">Doctor</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Phone</label>
                            <input className="w-full p-3 sm:p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="+1..." />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Verification Status</label>
                            <div className={`w-full p-3 sm:p-3.5 rounded-2xl border-2 cursor-pointer flex items-center justify-between transition-colors ${formData.isVerified ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-transparent text-slate-400'}`} onClick={() => setFormData({...formData, isVerified: !formData.isVerified})}>
                                <span className="font-bold text-xs sm:text-sm">{formData.isVerified ? 'Verified Profile' : 'Unverified'}</span>
                                <div className={`w-8 h-4 rounded-full relative transition-colors ${formData.isVerified ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                                    <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${formData.isVerified ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Account Status (Access)</label>
                            <div 
                                className={`w-full p-3 sm:p-3.5 rounded-2xl border-2 cursor-pointer flex items-center justify-between transition-colors ${
                                    formData.isDisabled 
                                        ? 'bg-rose-50 border-rose-200 text-rose-700' 
                                        : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                }`} 
                                onClick={() => setFormData({...formData, isDisabled: !formData.isDisabled})}
                                title="Click to toggle user enable/disable"
                            >
                                <span className="font-bold text-xs sm:text-sm flex items-center gap-2">
                                    <i className={`fa-solid ${formData.isDisabled ? 'fa-ban text-rose-600' : 'fa-circle-check text-emerald-600'}`}></i>
                                    {formData.isDisabled ? 'Account Disabled (Suspended)' : 'Account Active (Enabled)'}
                                </span>
                                <div className={`w-8 h-4 rounded-full relative transition-colors ${formData.isDisabled ? 'bg-rose-500' : 'bg-emerald-500'}`}>
                                    <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${formData.isDisabled ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {formData.role === 'doctor' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Medical License ID / Registration #</label>
                                <input 
                                    className="w-full p-3 sm:p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base text-slate-800" 
                                    value={formData.licenseNumber} 
                                    onChange={e => setFormData({...formData, licenseNumber: e.target.value})} 
                                    placeholder="e.g. MD-84920 or GMC-7491024" 
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Rovita Telehealth License ID</label>
                                <div className="w-full p-3 sm:p-3.5 bg-slate-100/90 rounded-2xl border border-slate-200 font-mono font-bold text-sm sm:text-base text-slate-700 flex items-center justify-between">
                                    <span>{user.rovitaLicenseId || formatHumaneRovitaLicenseId(user)}</span>
                                    <span className="text-[9px] font-bold bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-md">Verified ID</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-1">
                         <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Address</label>
                         <input className="w-full p-3 sm:p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Full Address" />
                    </div>

                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Bio</label>
                        <textarea className="w-full p-3 sm:p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-xs sm:text-sm h-28 resize-none leading-relaxed" value={formData.bio} onChange={e => setFormData({...formData, bio: e.target.value})} placeholder="Professional summary..." />
                    </div>

                    {formData.role === 'doctor' && (
                        <div className="space-y-1 pb-2">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Specialties</label>
                            <div className="flex flex-wrap gap-1.5 pt-1">
                                {availableSpecialties.map(s => (
                                    <button 
                                        key={s} 
                                        onClick={() => handleSpecialtyToggle(s)}
                                        className={`px-3 py-1.5 rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all ${
                                            formData.specialties.includes(s) ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-50 text-slate-400 border border-slate-100 hover:border-indigo-100'
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="p-3.5 sm:p-4 bg-slate-50 rounded-2xl border border-slate-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-2">
                        <div>
                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Account Security & Credentials</p>
                            <p className="text-xs font-bold text-slate-700 mt-0.5">Manage authentication password or dispatch reset links</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => onOpenPasswordModal && onOpenPasswordModal(user)}
                            className="px-3.5 py-2 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-black text-xs transition-all flex items-center gap-1.5 self-start sm:self-auto shrink-0 shadow-2xs"
                        >
                            <i className="fa-solid fa-key text-xs"></i>
                            Change Password
                        </button>
                    </div>
                </div>

                <div className="p-4 sm:p-6 bg-slate-50/90 backdrop-blur-md flex gap-3 sm:gap-4 shrink-0 border-t border-slate-100 z-10">
                    <Button onClick={onClose} variant="ghost" className="flex-1 py-3 font-bold text-sm">Discard Changes</Button>
                    <Button onClick={() => onSave(user.id, formData)} className="flex-[2] py-3 text-sm shadow-xl shadow-indigo-200 flex items-center justify-center gap-2">
                        <i className="fa-solid fa-floppy-disk"></i>
                        Save User Account
                    </Button>
                </div>
            </div>
        </div>
    );
};

const AppointmentEditModal = ({ appointment, onClose, onSave }: { appointment: Appointment, onClose: () => void, onSave: (id: string, data: Partial<Appointment>) => void }) => {
    const [formData, setFormData] = useState({
        date: appointment.date,
        time: appointment.time,
        status: appointment.status,
        notes: appointment.notes || ''
    });

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'auto'; };
    }, []);

    return (
        <div className="fixed inset-0 z-[600] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-lg rounded-[1.8rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col my-auto max-h-[calc(100dvh-1.25rem)] sm:max-h-[calc(100dvh-2rem)] md:max-h-[85vh] border border-slate-100 relative">
                <div className="p-4 sm:p-6 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900 tracking-tight">Edit Appointment</h3>
                        <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">Ref: {appointment.id.slice(0, 8)}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-300 hover:text-slate-500 transition-colors p-2 rounded-xl"><i className="fa-solid fa-xmark text-xl"></i></button>
                </div>
                
                <div className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-5 overflow-y-auto flex-1 scrollbar-thin min-h-0">
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Session Date</label>
                        <input type="date" className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Session Time</label>
                        <input type="time" className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" value={formData.time} onChange={e => setFormData({...formData, time: e.target.value})} />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Clinical Status</label>
                        <select className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base appearance-none bg-slate-50" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value as any})}>
                            <option value="upcoming">Upcoming</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Administrative Notes</label>
                        <textarea className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-xs sm:text-sm h-28 resize-none leading-relaxed" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Internal notes..." />
                    </div>
                </div>

                <div className="p-4 sm:p-6 bg-slate-50/90 backdrop-blur-md flex gap-3 sm:gap-4 shrink-0 border-t border-slate-100 z-10">
                    <Button onClick={onClose} variant="ghost" className="flex-1 font-bold py-3 text-sm">Discard</Button>
                    <Button onClick={() => onSave(appointment.id, formData)} className="flex-[2] shadow-xl shadow-indigo-100 flex items-center justify-center gap-2 py-3 text-sm">
                        <i className="fa-solid fa-calendar-check"></i>
                        Save Schedule
                    </Button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN DASHBOARD ---

export const AdminDashboard: React.FC<{ setPage: (p: string) => void }> = ({ setPage }) => {
    const { 
        allUsers, allAppointments, auditLogs, globalSettings, saveGlobalSettings, 
        manageUser, toggleUserDisabled, deleteUser, resetUserPassword, adminChangeUserPassword, adminUpdateAppointment, adminDeleteAppointment,
        contactMessages, deleteContactMessage, updateContactMessage, adminCreateUser, showToast, user, testSmtpConfig,
        checkAndDispatchAppointmentReminders
    } = useApp();
    
    const [tab, setTab] = useState<'general' | 'users' | 'appointments' | 'infra' | 'audit' | 'inbox'>('general');
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [passwordModalUser, setPasswordModalUser] = useState<User | null>(null);
    const [creatingUser, setCreatingUser] = useState(false);
    const [replyingMessage, setReplyingMessage] = useState<any | null>(null);
    const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
    const [smtp, setSmtp] = useState(globalSettings);
    const [financials, setFinancials] = useState(globalSettings);
    const [inboxSearch, setInboxSearch] = useState('');
    const [isTestingSmtp, setIsTestingSmtp] = useState(false);
    const [testFeedback, setTestFeedback] = useState<{ success: boolean; message: string; guidance?: string } | null>(null);

    const { replyToContactMessage } = useApp();

    useEffect(() => {
        setSmtp(globalSettings);
        setFinancials(globalSettings);
    }, [globalSettings]);

    const stats = [
        { label: 'Platform Users', val: allUsers.length, icon: 'fa-users', color: 'text-indigo-600' },
        { label: 'Appointment Volume', val: allAppointments.length, icon: 'fa-heart-pulse', color: 'text-rose-500' },
        { label: 'Active Doctors', val: allUsers.filter(u => u.role === 'doctor' && !isUserDisabled(u)).length, icon: 'fa-user-md', color: 'text-emerald-500' },
        { label: 'Pending Inquiries', val: contactMessages.filter(m => m.status !== 'read').length, icon: 'fa-envelope-open-text', color: 'text-indigo-500' }
    ];

    const handleToggle = (key: keyof GlobalSettings) => {
        const newVal = !(globalSettings as any)[key];
        saveGlobalSettings({ [key]: newVal });
    };

    const handleSyncInfra = async (type: 'financial' | 'smtp') => {
        if (type === 'financial') {
            await saveGlobalSettings({
                stripePublicKey: financials.stripePublicKey,
                stripeSecretKey: financials.stripeSecretKey,
                paypalClientId: financials.paypalClientId,
                paypalSecret: financials.paypalSecret
            });
            showToast("Financial infrastructure settings saved successfully!", "success");
        } else {
            const hostToSave = (!smtp.smtpHost || smtp.smtpHost === 'smtp.healthcare.org') && (smtp.smtpUser?.includes('@rovita.co') || smtp.smtpUser?.includes('@gmail.com'))
                ? 'smtp.gmail.com'
                : (smtp.smtpHost || 'smtp.gmail.com');
            const portToSave = smtp.smtpPort || '587';

            await saveGlobalSettings({
                smtpHost: hostToSave,
                smtpPort: portToSave,
                smtpUser: smtp.smtpUser,
                smtpPass: smtp.smtpPass
            });
            setSmtp(prev => ({ ...prev, smtpHost: hostToSave, smtpPort: portToSave }));
            showToast("Email (SMTP) notification settings saved successfully!", "success");
        }
    };

    const handleTestSmtp = async () => {
        if (!smtp.smtpUser || !smtp.smtpPass) {
            showToast("Please enter an email username and password first.", "warning");
            return;
        }
        setIsTestingSmtp(true);
        setTestFeedback(null);
        try {
            const hostToTest = (!smtp.smtpHost || smtp.smtpHost === 'smtp.healthcare.org') && (smtp.smtpUser?.includes('@rovita.co') || smtp.smtpUser?.includes('@gmail.com'))
                ? 'smtp.gmail.com'
                : (smtp.smtpHost || 'smtp.gmail.com');
            const portToTest = smtp.smtpPort || '587';

            const res = await testSmtpConfig({
                smtpHost: hostToTest,
                smtpPort: portToTest,
                smtpUser: smtp.smtpUser,
                smtpPass: smtp.smtpPass,
                testRecipient: user?.email || smtp.smtpUser
            });

            if (res.success) {
                showToast(res.message || "SMTP verified! Test email successfully delivered.", "success");
                setTestFeedback({ success: true, message: res.message || "Test email delivered successfully." });
            } else {
                showToast(res.error || "SMTP test failed. See guidance below.", "error");
                setTestFeedback({ success: false, message: res.error || "Connection test failed", guidance: res.guidance });
            }
        } catch (err: any) {
            showToast(err.message || "Network error testing SMTP", "error");
            setTestFeedback({ success: false, message: err.message || "Network error testing SMTP" });
        } finally {
            setIsTestingSmtp(false);
        }
    };

    return (
        <div className="space-y-12 animate-in fade-in duration-700">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((s, i) => (
                    <button 
                        key={i} 
                        onClick={() => {
                            if (s.label === 'Active Doctors') setPage('specialists');
                            else if (s.label === 'Platform Users') setTab('users');
                            else if (s.label === 'Appointment Volume') setTab('appointments');
                        }}
                        className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-50 group hover:shadow-xl transition-all duration-500 text-left"
                    >
                        <div className={`w-12 h-12 rounded-2xl bg-slate-50 ${s.color} flex items-center justify-center text-xl mb-4 group-hover:scale-110 transition-transform`}>
                            <i className={`fa-solid ${s.icon}`}></i>
                        </div>
                        <h4 className="text-4xl font-black text-slate-900 tracking-tighter">{s.val}</h4>
                        <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs font-black uppercase text-slate-400 tracking-[0.2em]">{s.label}</p>
                            <i className="fa-solid fa-arrow-right text-[10px] text-indigo-300 opacity-0 group-hover:opacity-100 transition-opacity"></i>
                        </div>
                    </button>
                ))}
            </div>

            {/* Navigation Tabs */}
            <div className="flex flex-wrap gap-2 p-2 bg-slate-100/50 rounded-3xl w-fit">
                {[
                    { id: 'general', label: 'Platform' },
                    { id: 'users', label: 'Users' },
                    { id: 'appointments', label: 'Appointments' },
                    { id: 'inbox', label: 'Inbox' },
                    { id: 'infra', label: 'Settings & Integrations' },
                    { id: 'audit', label: 'Audit Logs' }
                ].map(t => (
                    <button 
                        key={t.id} 
                        onClick={() => setTab(t.id as any)} 
                        className={`px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                            tab === t.id ? 'bg-white text-indigo-600 shadow-lg' : 'text-slate-400 hover:text-indigo-600'
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Content Area */}
            <div className="min-h-[500px]">
                {tab === 'general' && (
                    <div className="grid lg:grid-cols-2 gap-10">
                        <div className="bg-white p-12 rounded-[4rem] border border-slate-50 shadow-sm space-y-10">
                            <SectionTitle title="System Controls" subtitle="Manage website availability, payments, and assistant features." />
                            <div className="space-y-6">
                                {[
                                    { id: 'maintenanceMode', label: 'Maintenance Mode', desc: 'Temporarily pause patient bookings for system maintenance.' },
                                    { id: 'aiEngineActive', label: 'Health Assistant', desc: 'Enable guided symptom check and health tips.' },
                                    { id: 'paymentsEnabled', label: 'Online Payments', desc: 'Accept patient payments for consultations.' },
                                    { id: 'complimentaryAccessEnabled', label: 'Free Consultations', desc: 'Allow patients to book appointments free of charge.' }
                                ].map(toggle => (
                                    <div key={toggle.id} className="flex items-center justify-between p-6 bg-slate-50 rounded-3xl group hover:bg-slate-100/50 transition-colors">
                                        <div>
                                            <p className="font-black text-slate-900 text-lg">{toggle.label}</p>
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">{toggle.desc}</p>
                                        </div>
                                        <button 
                                            onClick={() => handleToggle(toggle.id as any)}
                                            className={`w-14 h-8 rounded-full p-1 flex items-center transition-all duration-300 ${
                                                (globalSettings as any)[toggle.id] ? 'bg-indigo-600' : 'bg-slate-300'
                                            }`}
                                        >
                                            <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                                                (globalSettings as any)[toggle.id] ? 'translate-x-6' : 'translate-x-0'
                                            }`}></div>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white p-12 rounded-[4rem] border border-slate-50 shadow-sm space-y-10">
                            <SectionTitle title="Branding & Organization" subtitle="Update platform name, contact email, and currency." />
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase text-slate-400 tracking-widest ml-4">Platform Name</label>
                                    <input 
                                        className="w-full p-5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-lg" 
                                        value={globalSettings.platformName} 
                                        onChange={e => saveGlobalSettings({ platformName: e.target.value })} 
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase text-slate-400 tracking-widest ml-4">Currency Code</label>
                                    <input 
                                        className="w-full p-5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold uppercase text-lg" 
                                        value={globalSettings.platformCurrency} 
                                        onChange={e => saveGlobalSettings({ platformCurrency: e.target.value })} 
                                    />
                                </div>
                                <div className="p-8 bg-indigo-50 rounded-3xl border border-indigo-100">
                                    <p className="text-xs font-black text-indigo-400 uppercase tracking-widest mb-2">Helpful Note</p>
                                    <p className="text-indigo-900 text-lg font-bold leading-relaxed">
                                        Changes take effect immediately across all patient and doctor pages without needing a server restart.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {tab === 'users' && (
                    <div className="bg-white p-12 rounded-[4rem] border border-slate-50 shadow-sm overflow-x-auto">
                        <div className="flex justify-between items-center mb-8">
                            <SectionTitle title="User Registry" subtitle="Manage patients, doctors, and staff access." />
                            <Button onClick={() => setCreatingUser(true)}>
                                <i className="fa-solid fa-plus mr-2"></i> Add New User
                            </Button>
                        </div>
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-slate-100">
                                    <th className="pb-8 text-xs font-black uppercase text-slate-400 tracking-widest">User Details</th>
                                    <th className="pb-8 text-xs font-black uppercase text-slate-400 tracking-widest">Role</th>
                                    <th className="pb-8 text-xs font-black uppercase text-slate-400 tracking-widest">Status</th>
                                    <th className="pb-8 text-xs font-black uppercase text-slate-400 tracking-widest">Specialties</th>
                                    <th className="pb-8 text-xs font-black uppercase text-slate-400 tracking-widest text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {allUsers.map(u => {
                                    const isUserDisabled = Boolean(u.isDisabled || u.disabled || u.status === 'suspended' || u.status === 'revoked');
                                    return (
                                    <tr key={u.id} className={`group hover:bg-slate-50/50 transition-colors ${isUserDisabled ? 'opacity-70 bg-rose-50/20' : ''}`}>
                                        <td className="py-6 flex items-center gap-4">
                                            <div className="relative">
                                                <img src={u.avatar} className="w-12 h-12 rounded-2xl bg-slate-100 shadow-sm" alt="Avatar" />
                                                {u.isVerified && <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-indigo-500 rounded-full border-2 border-white flex items-center justify-center text-[8px] text-white"><i className="fa-solid fa-check"></i></span>}
                                            </div>
                                            <div>
                                                <p className="font-black text-slate-900 flex items-center gap-2 text-lg">
                                                    {u.name.toLowerCase().startsWith('dr') ? u.name : (u.role === 'doctor' ? `Dr. ${u.name}` : u.name)}
                                                    {isUserDisabled && <span className="text-[10px] font-black text-rose-600 bg-rose-100 px-2 py-0.5 rounded-md uppercase">Suspended</span>}
                                                </p>
                                                <p className="text-sm font-bold text-slate-400">{u.email}</p>
                                                {u.mrn && <p className="text-xs font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md inline-block mt-1">{u.mrn}</p>}
                                            </div>
                                        </td>
                                        <td className="py-6">
                                            <span className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-tighter ${
                                                u.role === 'admin' ? 'bg-rose-50 text-rose-600' : u.role === 'doctor' ? 'bg-emerald-50 text-emerald-600' : 'bg-indigo-50 text-indigo-600'
                                            }`}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="py-6">
                                            {isUserDisabled ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-rose-100 text-rose-700 border border-rose-200">
                                                    <i className="fa-solid fa-ban text-[10px]"></i> Disabled
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-emerald-100 text-emerald-700 border border-emerald-200">
                                                    <i className="fa-solid fa-circle-check text-[10px]"></i> Active
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-6 max-w-[200px]">
                                            <div className="flex flex-wrap gap-1">
                                                {u.role === 'doctor' && (u.specialties?.length ? u.specialties.map(s => <span key={s} className="text-[10px] font-black bg-slate-100 px-2 py-0.5 rounded-lg border border-slate-200">{s}</span>) : 'GP')}
                                            </div>
                                        </td>
                                        <td className="py-6 text-right space-x-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => setEditingUser(u)} title="Edit User" aria-label="Edit User" className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all"><i className="fa-solid fa-pen-nib"></i></button>
                                            <button 
                                                onClick={() => toggleUserDisabled(u.id)} 
                                                title={isUserDisabled ? "Enable User Account" : "Disable User Account"} 
                                                aria-label={isUserDisabled ? "Enable User Account" : "Disable User Account"} 
                                                className={`w-10 h-10 rounded-xl transition-all shadow-xs ${
                                                    isUserDisabled 
                                                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white ring-2 ring-emerald-300' 
                                                        : 'bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white'
                                                }`}
                                            >
                                                <i className={`fa-solid ${isUserDisabled ? 'fa-user-check' : 'fa-user-slash'}`}></i>
                                            </button>
                                            <button onClick={() => setPasswordModalUser(u)} title="Change Password" aria-label="Change Password" className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all"><i className="fa-solid fa-key"></i></button>
                                            <button onClick={() => deleteUser(u.id)} title="Delete User" aria-label="Delete User" className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 hover:bg-rose-600 hover:text-white transition-all"><i className="fa-solid fa-trash-can"></i></button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === 'appointments' && (
                    <div className="bg-white p-12 rounded-[4rem] border border-slate-50 shadow-sm overflow-x-auto">
                        <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <SectionTitle title="All Appointments" subtitle="Manage and monitor scheduled consultations across all doctors and patients." />
                            <button
                                onClick={async () => {
                                    showToast("Checking Reminders", "Scanning upcoming appointments and dispatching email reminders...", "info");
                                    await checkAndDispatchAppointmentReminders();
                                    showToast("Reminders Dispatched", "All eligible upcoming appointment reminders have been processed.", "success");
                                }}
                                className="px-5 py-2.5 bg-indigo-50 hover:bg-indigo-600 hover:text-white text-indigo-700 font-black text-xs uppercase tracking-wider rounded-xl flex items-center gap-2 transition-all shadow-xs cursor-pointer active:scale-95 shrink-0"
                                title="Scan upcoming appointments and dispatch email reminders immediately"
                            >
                                <i className="fa-solid fa-bell"></i>
                                Dispatch Reminders Now
                            </button>
                        </div>
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-slate-100">
                                    <th className="pb-8 text-xs font-black uppercase text-slate-400 tracking-widest">Participants</th>
                                    <th className="pb-8 text-xs font-black uppercase text-slate-400 tracking-widest">Scheduled Time</th>
                                    <th className="pb-8 text-xs font-black uppercase text-slate-400 tracking-widest">Status</th>
                                    <th className="pb-8 text-xs font-black uppercase text-slate-400 tracking-widest text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {allAppointments.map(a => (
                                    <tr key={a.id} className="group hover:bg-slate-50/50 transition-colors">
                                        <td className="py-6">
                                            <div className="flex flex-col">
                                                <p className="font-black text-slate-900 text-lg">Patient: {a.patientName}</p>
                                                <p className="text-xs font-black text-indigo-600 uppercase tracking-widest">Clinician: {a.doctorName.toLowerCase().startsWith('dr') ? a.doctorName : `Dr. ${a.doctorName}`}</p>
                                            </div>
                                        </td>
                                        <td className="py-6">
                                            <p className="font-bold text-slate-800 text-base">{a.date}</p>
                                            <p className="text-xs font-black text-slate-400">{a.time}</p>
                                        </td>
                                        <td className="py-6">
                                            <span className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-tighter ${
                                                a.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : a.status === 'cancelled' ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-600'
                                            }`}>
                                                {a.status}
                                            </span>
                                        </td>
                                        <td className="py-6 text-right space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => setEditingAppointment(a)} title="Edit Slot" className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all"><i className="fa-solid fa-calendar-check"></i></button>
                                            <button onClick={() => adminDeleteAppointment(a.id)} title="Delete Record" className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 hover:bg-rose-600 hover:text-white transition-all"><i className="fa-solid fa-trash-can"></i></button>
                                        </td>
                                    </tr>
                                ))}
                                {allAppointments.length === 0 && (
                                    <tr><td colSpan={4} className="py-20 text-center text-slate-300 italic font-black uppercase text-xs tracking-widest">No global clinical records found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
                 {tab === 'inbox' && (
                    <div className="bg-white p-12 rounded-[4rem] border border-slate-50 shadow-sm space-y-10">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                            <SectionTitle title="Help Center Inbox" subtitle="Inquiries received from the public Contact page." />
                            <div className="relative w-full md:w-80">
                                <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-slate-300"></i>
                                <input 
                                    className="w-full pl-12 pr-4 py-3 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-[#008080] outline-none font-bold text-sm" 
                                    placeholder="Filter by MR Number..."
                                    value={inboxSearch}
                                    onChange={e => setInboxSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="space-y-4">
                            {contactMessages.filter(m => !inboxSearch || m.mrNumber?.toLowerCase().includes(inboxSearch.toLowerCase())).length === 0 ? (
                                <div className="py-32 text-center text-slate-300 italic font-black text-xs uppercase tracking-widest">No matching messages found.</div>
                            ) : (
                                contactMessages
                                    .filter(msg => !inboxSearch || msg.mrNumber?.toLowerCase().includes(inboxSearch.toLowerCase()))
                                    .map(msg => (
                                    <div key={msg.id} 
                                        onClick={() => msg.status !== 'read' && updateContactMessage(msg.id, 'read')}
                                        className={`p-8 rounded-[2.5rem] border transition-all cursor-pointer group ${msg.status === 'read' ? 'bg-slate-50/50 border-slate-100 opacity-80' : 'bg-white border-slate-100 shadow-md ring-2 ring-[#008080]/5'}`}>
                                        <div className="flex flex-col lg:flex-row justify-between lg:items-start mb-6 gap-6">
                                            <div className="flex items-center gap-5">
                                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-sm ${msg.status === 'read' ? 'bg-slate-100 text-slate-400' : 'bg-[#008080]/10 text-[#008080]'}`}>
                                                    <i className={`fa-solid ${msg.status === 'read' ? 'fa-envelope-open' : 'fa-envelope'}`}></i>
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-3">
                                                        <h4 className={`font-black text-xl ${msg.status === 'read' ? 'text-slate-600' : 'text-slate-900'}`}>{msg.name} {msg.surname}</h4>
                                                        {msg.isRegistered && <span className="bg-emerald-50 text-emerald-600 text-[10px] font-black px-2 py-0.5 rounded-lg uppercase tracking-widest border border-emerald-100">Registered</span>}
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                                                        <a href={`mailto:${encodeURIComponent(msg.email || '')}`} onClick={e => e.stopPropagation()} className="text-sm font-bold text-[#008080] hover:underline flex items-center gap-2"><i className="fa-solid fa-at opacity-50"></i>{msg.email}</a>
                                                        <span className="text-sm font-bold text-slate-500 flex items-center gap-2"><i className="fa-solid fa-phone opacity-50"></i>{msg.phone}</span>
                                                        {msg.mrNumber && <span className="text-sm font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md flex items-center gap-2"><i className="fa-solid fa-barcode opacity-50"></i>{msg.mrNumber}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-3 lg:self-start">
                                                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mr-4">{new Date(msg.timestamp).toLocaleString()}</p>
                                                
                                                <button onClick={(e) => { e.stopPropagation(); setReplyingMessage(msg); }} title="Reply via Platform" className="px-5 py-2.5 bg-[#008080] text-white rounded-xl flex items-center gap-2 hover:bg-[#006666] transition-all shadow-lg shadow-[#008080]/20 font-black text-xs uppercase tracking-widest">
                                                    <i className="fa-solid fa-reply"></i> Reply
                                                </button>

                                                {msg.status === 'read' ? (
                                                    <button onClick={(e) => { e.stopPropagation(); updateContactMessage(msg.id, 'new'); }} title="Mark as Unread" className="w-10 h-10 bg-white text-indigo-500 rounded-xl flex items-center justify-center hover:bg-indigo-50 transition-all shadow-sm border border-slate-100">
                                                        <i className="fa-solid fa-envelope"></i>
                                                    </button>
                                                ) : (
                                                    <button onClick={(e) => { e.stopPropagation(); updateContactMessage(msg.id, 'read'); }} title="Mark as Read" className="w-10 h-10 bg-white text-emerald-500 rounded-xl flex items-center justify-center hover:bg-emerald-50 transition-all shadow-sm border border-slate-100">
                                                        <i className="fa-solid fa-check"></i>
                                                    </button>
                                                )}
                                                <button onClick={(e) => { e.stopPropagation(); deleteContactMessage(msg.id); }} title="Delete Message" className="w-10 h-10 bg-white text-rose-500 rounded-xl flex items-center justify-center hover:bg-rose-50 hover:text-white transition-all shadow-sm border border-slate-100">
                                                    <i className="fa-solid fa-trash-can"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-inner group-hover:border-[#008080]/20 transition-all">
                                            <p className="text-slate-600 font-medium leading-relaxed text-xl mb-4 italic">"{msg.message}"</p>
                                            
                                            {msg.repliedAt && (
                                                <div className="mt-6 pt-6 border-t border-slate-50">
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-[10px]"><i className="fa-solid fa-reply"></i></div>
                                                        <p className="text-[10px] font-black uppercase text-emerald-600 tracking-widest underline decoration-2 underline-offset-4">Clinician Response Sent on {new Date(msg.repliedAt).toLocaleDateString()}</p>
                                                    </div>
                                                    <p className="text-slate-500 font-medium text-sm bg-slate-50/50 p-4 rounded-xl border border-slate-50">{msg.replyMessage}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {tab === 'infra' && (
                    <div className="grid lg:grid-cols-2 gap-10">
                        <div className="bg-white p-12 rounded-[4rem] border border-slate-50 shadow-sm space-y-10">
                            <SectionTitle title="Payment Gateway Settings" subtitle="Configure Stripe and PayPal to process appointment fees." />
                            <div className="space-y-10">
                                {/* Stripe Configuration */}
                                <div className="p-8 bg-slate-50 rounded-[3rem] border border-transparent hover:border-indigo-100 transition-all space-y-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-5">
                                            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-3xl text-indigo-500"><i className="fa-brands fa-cc-stripe"></i></div>
                                            <div>
                                                <p className="font-black text-slate-900 text-lg">Stripe Gateway</p>
                                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Status: {globalSettings.stripeEnabled ? 'Live' : 'Inactive'}</p>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={() => handleToggle('stripeEnabled')}
                                            className={`w-14 h-8 rounded-full p-1 flex items-center transition-all duration-300 ${
                                                globalSettings.stripeEnabled ? 'bg-indigo-600' : 'bg-slate-300'
                                            }`}
                                        >
                                            <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                                                globalSettings.stripeEnabled ? 'translate-x-6' : 'translate-x-0'
                                            }`}></div>
                                        </button>
                                    </div>
                                    {globalSettings.stripeEnabled && (
                                        <div className="grid gap-4 animate-in slide-in-from-top-2 duration-300">
                                            <input className="w-full p-4 bg-white rounded-xl border border-slate-200 outline-none font-bold text-base" placeholder="Stripe Public Key" value={financials.stripePublicKey || ''} onChange={e => setFinancials({...financials, stripePublicKey: e.target.value})} />
                                            <input className="w-full p-4 bg-white rounded-xl border border-slate-200 outline-none font-bold text-base" type="password" placeholder="Stripe Secret Key" value={financials.stripeSecretKey || ''} onChange={e => setFinancials({...financials, stripeSecretKey: e.target.value})} autoComplete="new-password" />
                                        </div>
                                    )}
                                </div>

                                {/* PayPal Configuration */}
                                <div className="p-8 bg-slate-50 rounded-[3rem] border border-transparent hover:border-indigo-100 transition-all space-y-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-5">
                                            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-3xl text-indigo-500"><i className="fa-brands fa-paypal"></i></div>
                                            <div>
                                                <p className="font-black text-slate-900 text-lg">PayPal Express</p>
                                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Status: {globalSettings.paypalEnabled ? 'Live' : 'Inactive'}</p>
                                            </div>
                                        </div>
                                        <button 
                                            type="button"
                                            onClick={() => handleToggle('paypalEnabled')}
                                            className={`w-14 h-8 rounded-full p-1 flex items-center transition-all duration-300 ${
                                                globalSettings.paypalEnabled ? 'bg-indigo-600' : 'bg-slate-300'
                                            }`}
                                        >
                                            <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                                                globalSettings.paypalEnabled ? 'translate-x-6' : 'translate-x-0'
                                            }`}></div>
                                        </button>
                                    </div>
                                    {globalSettings.paypalEnabled && (
                                        <div className="grid gap-4 animate-in slide-in-from-top-2 duration-300">
                                            <input className="w-full p-4 bg-white rounded-xl border border-slate-200 outline-none font-bold text-base" placeholder="PayPal Client ID" value={financials.paypalClientId || ''} onChange={e => setFinancials({...financials, paypalClientId: e.target.value})} />
                                            <input className="w-full p-4 bg-white rounded-xl border border-slate-200 outline-none font-bold text-base" type="password" placeholder="PayPal Secret" value={financials.paypalSecret || ''} onChange={e => setFinancials({...financials, paypalSecret: e.target.value})} autoComplete="new-password" />
                                        </div>
                                    )}
                                </div>
                                <Button type="button" onClick={() => handleSyncInfra('financial')} className="w-full py-6 text-xl shadow-2xl shadow-indigo-100 hover:shadow-indigo-200 transition-all flex items-center justify-center gap-3">
                                    <i className="fa-solid fa-check"></i>
                                    Save Payment Settings
                                </Button>
                            </div>
                        </div>

                        <div className="bg-white p-12 rounded-[4rem] border border-slate-50 shadow-sm space-y-8">
                            <div className="flex justify-between items-center">
                                <SectionTitle title="Email Notifications (SMTP)" subtitle="Set up email delivery for appointment confirmations and reminders." />
                                <div className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest border ${
                                    globalSettings.smtpHost 
                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-100' 
                                    : 'bg-amber-50 text-amber-600 border-amber-100'
                                }`}>
                                    {globalSettings.smtpHost ? 'Connected' : 'Not Configured'}
                                </div>
                            </div>

                            {/* Google Workspace & Gmail Guidance */}
                            <div className="p-5 bg-indigo-50/70 rounded-2xl border border-indigo-100/70 text-xs text-indigo-950 leading-relaxed flex items-start gap-3.5">
                                <i className="fa-solid fa-circle-info text-indigo-600 mt-1 text-base shrink-0"></i>
                                <div className="space-y-1">
                                    <p className="font-bold text-indigo-900 text-sm">Using Google Workspace or Gmail (@rovita.co, @gmail.com)?</p>
                                    <p className="text-slate-600 leading-normal">
                                        Set Host to <code className="bg-white px-1.5 py-0.5 rounded border border-indigo-200 font-mono text-[11px] font-bold text-indigo-700">smtp.gmail.com</code> and Port to <code className="bg-white px-1.5 py-0.5 rounded border border-indigo-200 font-mono text-[11px] font-bold text-indigo-700">587</code>.
                                    </p>
                                    <p className="text-slate-600 leading-normal">
                                        Google accounts with 2-Step Verification require a <strong>16-character App Password</strong> rather than your regular account password:
                                        Visit <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline font-bold hover:text-indigo-800">Google Account &gt; Security &gt; 2-Step Verification &gt; App Passwords</a>, create a password for "Mail", and paste the generated 16-character code below.
                                    </p>
                                </div>
                            </div>

                            {testFeedback && (
                                <div className={`p-5 rounded-2xl border text-xs leading-relaxed flex items-start gap-3.5 animate-in fade-in duration-300 ${
                                    testFeedback.success 
                                    ? 'bg-emerald-50 text-emerald-900 border-emerald-200' 
                                    : 'bg-rose-50 text-rose-950 border-rose-200'
                                }`}>
                                    <i className={`fa-solid ${testFeedback.success ? 'fa-circle-check text-emerald-600' : 'fa-circle-exclamation text-rose-600'} mt-0.5 text-base shrink-0`}></i>
                                    <div className="space-y-1.5 flex-1">
                                        <p className="font-bold text-sm">{testFeedback.success ? 'Connection Verified' : 'Connection Test Failed'}</p>
                                        <p className="text-slate-700">{testFeedback.message}</p>
                                        {testFeedback.guidance && (
                                            <div className="mt-2 p-3 bg-white/80 rounded-xl border border-rose-200/80 text-rose-900 font-medium">
                                                <strong>Action needed:</strong> {testFeedback.guidance}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <form onSubmit={(e) => { e.preventDefault(); handleSyncInfra('smtp'); }} className="grid gap-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase text-slate-400 tracking-widest ml-4">Mail Server Host</label>
                                    <input className="w-full p-5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-lg" placeholder="smtp.gmail.com" value={smtp.smtpHost || ''} onChange={e => setSmtp({...smtp, smtpHost: e.target.value})} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase text-slate-400 tracking-widest ml-4">Port</label>
                                        <input className="w-full p-5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-lg" placeholder="587" value={smtp.smtpPort || ''} onChange={e => setSmtp({...smtp, smtpPort: e.target.value})} />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-black uppercase text-slate-400 tracking-widest ml-4">Username / Email</label>
                                        <input className="w-full p-5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-lg" placeholder="info@rovita.care" value={smtp.smtpUser || ''} onChange={e => setSmtp({...smtp, smtpUser: e.target.value})} autoComplete="username" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase text-slate-400 tracking-widest ml-4">Password / Google App Password</label>
                                    <input className="w-full p-5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-lg" type="password" placeholder="••••••••" value={smtp.smtpPass || ''} onChange={e => setSmtp({...smtp, smtpPass: e.target.value})} autoComplete="current-password" />
                                </div>
                                <div className="flex flex-col sm:flex-row gap-4 mt-4">
                                    <button 
                                        type="button" 
                                        onClick={handleTestSmtp} 
                                        disabled={isTestingSmtp || !smtp.smtpUser || !smtp.smtpPass}
                                        className="flex-1 py-5 px-6 rounded-2xl font-bold text-base bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-all border border-indigo-200/60 flex items-center justify-center gap-2.5 disabled:opacity-50"
                                    >
                                        {isTestingSmtp ? (
                                            <>
                                                <i className="fa-solid fa-circle-notch fa-spin"></i>
                                                Testing Connection...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-paper-plane"></i>
                                                Test Connection
                                            </>
                                        )}
                                    </button>
                                    <Button type="submit" className="flex-[1.5] py-5 text-lg shadow-xl shadow-indigo-100 hover:shadow-indigo-200 transition-all flex items-center justify-center gap-3">
                                        <i className="fa-solid fa-floppy-disk"></i>
                                        Save Email Settings
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {tab === 'audit' && (
                    <div className="bg-white p-12 rounded-[4rem] border border-slate-50 shadow-sm space-y-10">
                        <SectionTitle title="Activity & Audit Logs" subtitle="System records and platform activity history." />
                        <div className="space-y-4">
                            {auditLogs.length === 0 ? (
                                <div className="py-32 text-center text-slate-300 italic font-black text-xs uppercase tracking-widest">No recent audit activity.</div>
                            ) : (
                                auditLogs.map(log => (
                                    <div key={log.id} className="flex justify-between items-center p-6 bg-slate-50 rounded-2xl border border-transparent hover:border-indigo-100 transition-all">
                                        <div className="flex items-center gap-5">
                                            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-400 border border-slate-100 shadow-sm"><i className="fa-solid fa-fingerprint"></i></div>
                                            <div>
                                                <p className="font-bold text-slate-800 text-lg">{log.action}</p>
                                                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mt-1">Operator: {log.userName}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs font-black text-indigo-400 tracking-widest">{new Date(log.timestamp).toLocaleDateString()}</p>
                                            <p className="text-xs font-black text-slate-300 tracking-widest">{new Date(log.timestamp).toLocaleTimeString()}</p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {creatingUser && (
                <UserCreateModal 
                    onClose={() => setCreatingUser(false)}
                    onSave={async (data) => {
                        await adminCreateUser(data.email, data.password, data.name, data.role);
                        setCreatingUser(false);
                    }}
                />
            )}
            {replyingMessage && (
                <ReplyModal 
                    message={replyingMessage}
                    onClose={() => setReplyingMessage(null)}
                    onReply={async (id, email, reply) => {
                        await replyToContactMessage(id, email, reply);
                    }}
                />
            )}
            {editingUser && (
                <UserEditModal 
                    user={editingUser} 
                    onClose={() => setEditingUser(null)} 
                    onSave={async (id, data) => {
                        await manageUser(id, data);
                        setEditingUser(null);
                    }}
                    onOpenPasswordModal={(u) => {
                        setPasswordModalUser(u);
                    }}
                />
            )}
            {passwordModalUser && (
                <AdminPasswordModal
                    user={passwordModalUser}
                    isOpen={Boolean(passwordModalUser)}
                    onClose={() => setPasswordModalUser(null)}
                    onSavePassword={async (userId, newPass, sendEmail) => {
                        await adminChangeUserPassword(userId, newPass, sendEmail);
                    }}
                    onSendResetEmail={async (email) => {
                        await resetUserPassword(email);
                    }}
                />
            )}
            {editingAppointment && (
                <AppointmentEditModal 
                    appointment={editingAppointment} 
                    onClose={() => setEditingAppointment(null)} 
                    onSave={async (id, data) => {
                        await adminUpdateAppointment(id, data);
                        setEditingAppointment(null);
                    }} 
                />
            )}
        </div>
    );
};
